'use client';

import Image from '@/components/Image';
import { useAppNavigation } from '@/hooks/useAppNavigation';
import { useProductImages } from '@/hooks/useProductImages';
import { useStore } from '@/providers/app-store-provider';
import { EProductImageType } from '@/utils/enums/goods';
import { formatPriceNumberWithSymbol, priceInfo } from '@/utils/price';
import { useTranslations } from 'next-intl';
import { useEffect, useState } from 'react';
import { useInView } from 'react-intersection-observer';

// 商品数据接口
interface GoodItemProduct {
  id?: number | string;
  name?: string;
  short_name?: string;
  shop_sku?: string;
  business_sku?: string;
  image?: {
    product?: string[];
    product_gray?: string[];
  };
  price_info?: any[];
  [key: string]: any;
}

interface GoodItemPcProps {
  product: GoodItemProduct;
  index: number;
  onProductClick?: (product: GoodItemProduct, index: number) => void;
  onProductExp?: (product: GoodItemProduct, index: number) => void;
}

export default function GoodItemPc({
  product,
  index,
  onProductClick,
  onProductExp,
}: GoodItemPcProps) {
  const { navigate } = useAppNavigation();
  const currency = useStore((s) => s.currency);
  const userInfo = useStore((s) => s.userInfo);
  const t = useTranslations('product');

  // hover状态管理
  const [isHovered, setIsHovered] = useState(false);

  // 使用 useProductImages hook 获取商品图片
  const { productImage } = useProductImages({
    productData: product as any,
    imageRule: [EProductImageType.PRODUCT, EProductImageType.MATERIAL, EProductImageType.CELEBRITY],
  });

  // 获取第三张灰底图
  const thirdImage =
    product.image?.product_gray?.[2] || product.image?.product?.[2] || productImage.src;

  // 曝光检测
  const { ref: expRef, inView } = useInView({
    threshold: 0.8,
    triggerOnce: true,
  });

  useEffect(() => {
    if (inView) {
      onProductExp?.(product, index);
    }
  }, [inView]);

  // 判断是否售罄
  const isSoldOut = product.stock <= 0;

  // 处理商品点击
  const toDetail = (item: GoodItemProduct, itemIndex: number) => {
    // 触发点击回调
    onProductClick?.(item, itemIndex);

    // 跳转到商品详情页
    if (item?.shop_sku) {
      navigate(`/goods-detail/${item.shop_sku}`);
    }
  };

  return (
    <a
      href={`/goods-detail/${product.shop_sku}`}
      onClick={(e) => {
        e.preventDefault();
        toDetail(product, index);
      }}
    >
      <div
        ref={expRef}
        className="rounded-[12px] overflow-hidden bg-[#EEEEEE] p-[24px] flex flex-col relative"
        style={{ aspectRatio: '447/514' }}
        onMouseEnter={() => setIsHovered(true)}
        onMouseLeave={() => setIsHovered(false)}
      >
        <div className="img-wrap flex-1 flex items-center justify-center w-full overflow-hidden relative px-[22px]">
          {/* 主图片 */}
          <Image
            src={isHovered ? thirdImage : productImage.src}
            alt={`${product.name || ''}`}
            width={356}
            height={356}
            className={`w-full h-auto max-w-[24.72vw] max-h-full object-contain transition-opacity duration-300 ${
              !productImage.isGray && productImage.type === EProductImageType.PRODUCT
                ? 'mix-blend-multiply'
                : ''
            } ${product.status === 2 || product.stock <= 0 ? 'opacity-50' : ''}`}
          />
        </div>
        <div className="price-info flex flex-col justify-center items-start h-auto">
          <p
            className="text-left text-[#808080] mb-[0.28vw]"
            style={{
              fontSize: '16px',
              lineHeight: '19px',
            }}
          >
            {product.short_name}
          </p>
          <div className="price flex items-center justify-start">
            {(() => {
              const p = priceInfo(userInfo, product, currency) || {};
              const now = formatPriceNumberWithSymbol(
                String(p?.sell_price || p?.price || '0'),
                currency,
              );
              const old =
                p && p.price && p.sell_price && p.sell_price !== p.price
                  ? formatPriceNumberWithSymbol(String(p.price), currency)
                  : '';
              return (
                <>
                  <span
                    className="now-price notranslate text-[#282828] font-bb"
                    style={{
                      fontSize: '24px',
                      lineHeight: '28px',
                    }}
                  >
                    {now}
                  </span>
                  {old && (
                    <span
                      className="old-price notranslate ml-[0.57vw] text-[#A9A9A9] line-through"
                      style={{
                        fontSize: '16px',
                        lineHeight: '19px',
                      }}
                    >
                      {old}
                    </span>
                  )}
                </>
              );
            })()}
          </div>
        </div>

        {/* 售罄遮罩 */}
        {isSoldOut && (
          <div className="absolute inset-0 bg-white/40 rounded-[12px] cursor-default">
            <span className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 flex items-center justify-center w-[27%] aspect-square rounded-full bg-black/60 text-center">
              <div
                className="text-white whitespace-pre-wrap text-[1.11vw]"
                dangerouslySetInnerHTML={{ __html: t('soldOut.soldOut') }}
              />
            </span>
          </div>
        )}
      </div>
    </a>
  );
}
